// Universal sortable-table primitive — the house standard for every data table.
// A hook that sorts rows by any registered column + a header cell that toggles it.
//
//   const cols = [
//     { key: 'symbol', type: 'string', get: r => r.symbol },
//     { key: 'drift',  type: 'number', get: r => r.drift },
//     { key: 'active', type: 'abs',    get: r => r.active },   // sort by |value|
//   ];
//   const { sorted, toggle, indicator } = useSortableRows(rows, cols, { key: 'drift', dir: 'desc' });
//   ...
//   <SortableTh label="Drift" colKey="drift" toggle={toggle} indicator={indicator} />
//   {sorted.map(...)}
//
// Column `type`:  'string' (localeCompare), 'number' (numeric), 'abs' (by magnitude).
// Toggling a new column defaults to asc for strings, desc for numbers/abs; re-clicking flips.
import React, { useMemo, useState } from 'react';

export function useSortableRows(rows, columns, initial = {}) {
  const colMap = useMemo(
    () => Object.fromEntries((columns || []).map((c) => [c.key, c])),
    [columns],
  );
  const [sortKey, setSortKey] = useState(initial.key || null);
  const [sortDir, setSortDir] = useState(initial.dir || 'desc');

  const toggle = (k) => {
    if (!colMap[k]) return;
    if (sortKey === k) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      setSortDir(colMap[k].type === 'string' ? 'asc' : 'desc');
    }
  };

  const indicator = (k) => (sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const sorted = useMemo(() => {
    const c = colMap[sortKey];
    if (!c) return rows;
    const arr = [...(rows || [])];
    arr.sort((a, b) => {
      if (c.type === 'string') {
        const av = (c.get(a) ?? '').toString();
        const bv = (c.get(b) ?? '').toString();
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const f = c.type === 'abs' ? Math.abs : (x) => x;
      const av = f(Number(c.get(a) ?? 0));
      const bv = f(Number(c.get(b) ?? 0));
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [rows, sortKey, sortDir, colMap]);

  return { sorted, sortKey, sortDir, toggle, indicator };
}

// Clickable header cell with a sort arrow. `align` matches the column's body cells.
export function SortableTh({ label, colKey, toggle, indicator, align = 'right', className = '' }) {
  const alignCls = align === 'left' ? 'text-left' : align === 'center' ? 'text-center' : 'text-right';
  return (
    <th
      onClick={() => toggle(colKey)}
      className={`${alignCls} cursor-pointer select-none hover:text-gray-600 dark:hover:text-gray-300 ${className}`}
      title="Sort by this column"
    >
      {label}<span className="text-brand-gold">{indicator(colKey)}</span>
    </th>
  );
}
